import type { Marker } from "./map";

import { Color, getFilterValuesForHex } from "./color";

const BASE_PIN_WIDTH = 32;
const WORLD_SIZE = 256;
const ATLAS_SIZE = 1024;
const ATLAS_GAP = 2;
const TOUCH_SLOP = 12;
const DROP_DELAY_MS = 500;
const DROP_DURATION_MS = 500;

type Keyframes = [progress: number, value: number][];
const DROP_OFFSET: Keyframes = [
	[0, -200],
	[0.5, 0],
	[0.65, -17],
	[0.75, -22],
	[1, 0],
];
const DROP_SCALE_Y: Keyframes = [
	[0, 0.9],
	[0.5, 1],
	[0.65, 0.9],
	[0.75, 0.9],
	[1, 1],
];
const DROP_OPACITY: Keyframes = [
	[0, 0],
	[0.05, 0.7],
	[0.5, 1],
	[1, 1],
];

export interface Pin {
	marker: Marker;
	worldX: number;
	worldY: number;
	width: number;
	image: string;
	color: string | undefined;
	filter: string | undefined;
	spriteKey: string;
}

export interface DrawnPin {
	pin: Pin;
	left: number;
	top: number;
	height: number;
}

interface Sprite {
	source: HTMLCanvasElement;
	x: number;
	y: number;
	width: number;
	height: number;
	/** Height over width of the source image; pins keep it the way `img { height: auto }` did. */
	aspect: number;
}

/**
 * Packs every tinted icon into shared canvases. Drawing pins from many small images makes the GPU switch textures on
 * almost every pin, which drops a rainbow layer of a few thousand pins to single-digit fps.
 */
class SpriteAtlas {
	private page: HTMLCanvasElement | undefined;
	private x = 0;
	private y = 0;
	private rowHeight = 0;

	allocate(width: number, height: number, aspect: number): Sprite {
		if (this.x + width + ATLAS_GAP > ATLAS_SIZE) {
			this.x = 0;
			this.y += this.rowHeight;
			this.rowHeight = 0;
		}
		if (!this.page || this.y + height + ATLAS_GAP > ATLAS_SIZE) {
			this.page = document.createElement("canvas");
			this.page.width = this.page.height = ATLAS_SIZE;
			this.x = this.y = this.rowHeight = 0;
		}
		const sprite = { source: this.page, x: this.x, y: this.y, width, height, aspect };
		this.x += width + ATLAS_GAP;
		this.rowHeight = Math.max(this.rowHeight, height + ATLAS_GAP);
		return sprite;
	}
}

export interface PinLayer {
	setPins(markers: Marker[]): void;
	remove(): void;
}

export function pinWidth(marker: Pick<Marker, "scale">) {
	return Math.round(BASE_PIN_WIDTH * (marker.scale ?? 1));
}

/** Web Mercator world coordinates, the space Google Maps projects into at zoom 0. */
export function toWorldPoint(lat: number, lng: number) {
	const sin = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
	return {
		x: WORLD_SIZE * (0.5 + lng / 360),
		y: WORLD_SIZE * (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)),
	};
}

/** The hex color the black pin artwork is recolored to, if any. */
export function pinColor(marker: Pick<Marker, "color" | "group">) {
	if (marker.color) return marker.color;
	return marker.group ? Color.fromString(marker.group).toHex() : undefined;
}

/** The CSS filter that recolors the black pin artwork, in the order the solver's values expect. */
export function pinFilter(marker: Pick<Marker, "color" | "group">) {
	const color = pinColor(marker);
	if (!color) return undefined;
	const { values } = getFilterValuesForHex(color);
	return [
		`brightness(${values.brightness.toFixed(2)}%)`,
		`invert(${values.invert.toFixed(2)}%)`,
		`sepia(${values.sepia.toFixed(2)}%)`,
		`saturate(${values.saturate.toFixed(2)}%)`,
		`hue-rotate(${(values.hueRotate * 3.6).toFixed(2)}deg)`,
		`contrast(${values.contrast.toFixed(2)}%)`,
	].join(" ");
}

let canvasFilterSupport: boolean | undefined;

/** Safari (through 26) ignores `context.filter`, so pins are recolored pixel by pixel there instead. */
function supportsCanvasFilter() {
	if (canvasFilterSupport === undefined) {
		const canvas = document.createElement("canvas");
		canvas.width = canvas.height = 1;
		const context = canvas.getContext("2d");
		if (!context) return false;
		context.filter = "invert(100%)";
		context.fillStyle = "#000000";
		context.fillRect(0, 0, 1, 1);
		canvasFilterSupport = context.getImageData(0, 0, 1, 1).data[0] > 127;
	}
	return canvasFilterSupport;
}

/** Applies the same filter chain as `pinFilter` to RGBA pixels, leaving alpha alone. */
export function tintPixels(pixels: Uint8ClampedArray, hex: string) {
	const { values } = getFilterValuesForHex(hex);
	const tinted = new Map<number, [number, number, number]>();
	for (let i = 0; i < pixels.length; i += 4) {
		if (pixels[i + 3] === 0) continue;
		const key = (pixels[i] << 16) | (pixels[i + 1] << 8) | pixels[i + 2];
		let rgb = tinted.get(key);
		if (!rgb) {
			const color = new Color("#000000");
			color.rgbValues = { r: pixels[i], g: pixels[i + 1], b: pixels[i + 2] };
			color.brightness(values.brightness / 100);
			color.invert(values.invert / 100);
			color.sepia(values.sepia / 100);
			color.saturate(values.saturate / 100);
			color.hueRotate(values.hueRotate * 3.6);
			color.contrast(values.contrast / 100);
			rgb = [Math.round(color.rgbValues.r), Math.round(color.rgbValues.g), Math.round(color.rgbValues.b)];
			tinted.set(key, rgb);
		}
		pixels[i] = rgb[0];
		pixels[i + 1] = rgb[1];
		pixels[i + 2] = rgb[2];
	}
}

function drawTinted(
	context: CanvasRenderingContext2D,
	image: HTMLImageElement,
	pin: Pick<Pin, "color" | "filter">,
	x: number,
	y: number,
	width: number,
	height: number,
) {
	if (pin.filter && supportsCanvasFilter()) {
		context.filter = pin.filter;
		context.drawImage(image, x, y, width, height);
		context.filter = "none";
		return;
	}
	context.drawImage(image, x, y, width, height);
	if (!pin.color) return;
	const data = context.getImageData(x, y, width, height);
	tintPixels(data.data, pin.color);
	context.putImageData(data, x, y);
}

/** Pins further south are drawn later, so they overlap the ones above them like Google's own markers. */
export function toPins(markers: Marker[]): Pin[] {
	return markers
		.map((marker) => {
			const world = toWorldPoint(marker.lat, marker.lon ?? marker.lng!);
			const width = pinWidth(marker);
			const color = pinColor(marker);
			return {
				marker,
				worldX: world.x,
				worldY: world.y,
				width,
				image: marker.image,
				color,
				filter: pinFilter(marker),
				spriteKey: `${marker.image}|${color ?? ""}|${width}`,
			};
		})
		.sort((a, b) => a.worldY - b.worldY);
}

/** A standalone copy of a pin as it looks on the map, for places a CSS or canvas filter can't reach (Street View). */
export async function createPinIcon(marker: Marker, width: number) {
	const image = new Image();
	image.src = marker.image;
	await image.decode();
	const height = Math.round((width * image.naturalHeight) / image.naturalWidth);
	const color = pinColor(marker);
	if (!color) return { url: marker.image, width, height };

	const ratio = window.devicePixelRatio || 1;
	const canvas = document.createElement("canvas");
	canvas.width = Math.round(width * ratio);
	canvas.height = Math.round(height * ratio);
	const context = canvas.getContext("2d")!;
	drawTinted(context, image, { color, filter: pinFilter(marker) }, 0, 0, canvas.width, canvas.height);
	return { url: canvas.toDataURL(), width, height };
}

export interface DropFrame {
	offsetY: number;
	scaleY: number;
	opacity: number;
}

/** The `drop` keyframes from index.css, which interpolate each property linearly between the stops that set it. */
export function dropFrame(elapsedMs: number): DropFrame {
	if (elapsedMs < DROP_DELAY_MS) return { offsetY: 0, scaleY: 1, opacity: 0 };
	const progress = Math.min((elapsedMs - DROP_DELAY_MS) / DROP_DURATION_MS, 1);
	return {
		offsetY: interpolate(DROP_OFFSET, progress),
		scaleY: interpolate(DROP_SCALE_Y, progress),
		opacity: interpolate(DROP_OPACITY, progress),
	};
}

function interpolate(keyframes: Keyframes, progress: number) {
	const next = keyframes.findIndex(([at]) => at >= progress);
	if (next <= 0) return keyframes[0][1];
	const [fromAt, from] = keyframes[next - 1];
	const [toAt, to] = keyframes[next];
	return from + ((to - from) * (progress - fromAt)) / (toAt - fromAt);
}

/** The top-most pin under the point; failing that, the nearest within `slop` pixels so small pins stay tappable by finger. */
export function findPinAt(drawn: DrawnPin[], x: number, y: number, slop = 0) {
	let nearest: DrawnPin | undefined;
	let nearestDistance = Infinity;
	for (let i = drawn.length - 1; i >= 0; i--) {
		const { pin, left, top, height } = drawn[i];
		const dx = Math.max(left - x, 0, x - (left + pin.width));
		const dy = Math.max(top - y, 0, y - (top + height));
		if (dx === 0 && dy === 0) return drawn[i];

		const distance = dx * dx + dy * dy;
		if (dx <= slop && dy <= slop && distance < nearestDistance) {
			nearest = drawn[i];
			nearestDistance = distance;
		}
	}
	return nearest;
}

/**
 * Draws every pin onto one canvas. Google repositions each DOM marker on every frame of a pan or zoom, which drops
 * a map with a few thousand pins to around 10 fps; a single canvas redraw per frame keeps it near 60.
 */
export function createPinLayer(
	map: google.maps.Map,
	onPinClick: (marker: Marker, anchorHeight: number) => void,
): PinLayer {
	class CanvasPinLayer extends google.maps.OverlayView {
		private readonly canvas = document.createElement("canvas");
		private readonly atlas = new SpriteAtlas();
		private readonly sprites = new Map<string, Sprite>();
		private readonly images = new Map<string, HTMLImageElement>();
		private listeners: google.maps.MapsEventListener[] = [];
		private pins: Pin[] = [];
		private drawn: DrawnPin[] = [];
		private hovered: Marker | undefined;
		private redrawQueued = false;
		private readonly animateDrops = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
		private readonly touchSlop = window.matchMedia?.("(pointer: coarse)").matches ? TOUCH_SLOP : 0;
		private readonly dropStarts = new Map<Marker["id"], number>();
		private dropsEndAt = 0;

		setPins(markers: Marker[]) {
			const previousIds = new Set(this.pins.map((pin) => pin.marker.id));
			this.pins = toPins(markers);

			if (this.animateDrops) {
				const now = performance.now();
				for (const pin of this.pins) {
					if (!previousIds.has(pin.marker.id)) this.dropStarts.set(pin.marker.id, now);
				}
				if (this.dropStarts.size) this.dropsEndAt = now + DROP_DELAY_MS + DROP_DURATION_MS;
			}
			this.draw();
		}

		onAdd() {
			Object.assign(this.canvas.style, { position: "absolute", pointerEvents: "none" });
			this.getPanes()?.overlayLayer.appendChild(this.canvas);
			this.listeners = [
				map.addListener("click", (event: google.maps.MapMouseEvent) => {
					const hit = this.pinAt(event.latLng);
					if (hit) onPinClick(hit.pin.marker, hit.height);
				}),
				map.addListener("mousemove", (event: google.maps.MapMouseEvent) =>
					this.hover(this.pinAt(event.latLng)?.pin.marker),
				),
				map.addListener("mouseout", () => this.hover(undefined)),
			];
		}

		onRemove() {
			this.hover(undefined);
			this.listeners.forEach((listener) => listener.remove());
			this.canvas.remove();
		}

		draw() {
			const projection = this.getProjection();
			const center = map.getCenter();
			if (!projection || !center) return;

			const container = map.getDiv();
			const width = container.clientWidth;
			const height = container.clientHeight;
			const ratio = window.devicePixelRatio || 1;
			const centerInPane = projection.fromLatLngToDivPixel(center)!;
			const centerOnScreen = projection.fromLatLngToContainerPixel(center)!;

			if (this.canvas.width !== Math.round(width * ratio) || this.canvas.height !== Math.round(height * ratio)) {
				this.canvas.width = Math.round(width * ratio);
				this.canvas.height = Math.round(height * ratio);
				this.canvas.style.width = `${width}px`;
				this.canvas.style.height = `${height}px`;
			}
			this.canvas.style.left = `${centerInPane.x - centerOnScreen.x}px`;
			this.canvas.style.top = `${centerInPane.y - centerOnScreen.y}px`;

			const context = this.canvas.getContext("2d")!;
			context.setTransform(ratio, 0, 0, ratio, 0, 0);
			context.clearRect(0, 0, width, height);

			const now = performance.now();
			const dropping = now < this.dropsEndAt;
			if (!dropping) this.dropStarts.clear();

			const toScreen = this.screenPosition(projection, center, centerOnScreen);
			const drawn: DrawnPin[] = [];
			for (const pin of this.pins) {
				const sprite = this.sprite(pin, ratio);
				if (!sprite) continue;

				const { x, y } = toScreen(pin);
				const pinHeight = pin.width * sprite.aspect;
				const left = x - pin.width / 2;
				const top = y - pinHeight;
				if (left > width || top > height || left + pin.width < 0 || y < 0) continue;

				const dropStart = dropping ? this.dropStarts.get(pin.marker.id) : undefined;
				const frame = dropStart === undefined ? undefined : dropFrame(now - dropStart);
				const drawHeight = pinHeight * (frame?.scaleY ?? 1);
				const drawTop = top + (pinHeight - drawHeight) / 2 + (frame?.offsetY ?? 0);
				context.globalAlpha = frame?.opacity ?? 1;
				context.drawImage(
					sprite.source,
					sprite.x,
					sprite.y,
					sprite.width,
					sprite.height,
					left,
					drawTop,
					pin.width,
					drawHeight,
				);
				drawn.push({ pin, left, top, height: pinHeight });
			}
			context.globalAlpha = 1;
			this.drawn = drawn;
			if (dropping) this.queueRedraw();
		}

		private screenPosition(
			projection: google.maps.MapCanvasProjection,
			center: google.maps.LatLng,
			centerOnScreen: google.maps.Point,
		) {
			if (map.getHeading() || map.getTilt()) {
				return (pin: Pin) =>
					projection.fromLatLngToContainerPixel(
						new google.maps.LatLng(pin.marker.lat, pin.marker.lon ?? pin.marker.lng!),
					)!;
			}

			const centerWorld = toWorldPoint(center.lat(), center.lng());
			const step = center.lng() > 0 ? -1 : 1;
			const neighbor = projection.fromLatLngToContainerPixel(
				new google.maps.LatLng(center.lat(), center.lng() + step),
			)!;
			const scale = (neighbor.x - centerOnScreen.x) / ((WORLD_SIZE / 360) * step);
			return (pin: Pin) => {
				let dx = pin.worldX - centerWorld.x;
				if (dx > WORLD_SIZE / 2) dx -= WORLD_SIZE;
				else if (dx < -WORLD_SIZE / 2) dx += WORLD_SIZE;
				return { x: centerOnScreen.x + dx * scale, y: centerOnScreen.y + (pin.worldY - centerWorld.y) * scale };
			};
		}

		private sprite(pin: Pin, ratio: number) {
			const cached = this.sprites.get(pin.spriteKey);
			if (cached) return cached;

			const image = this.image(pin.image);
			if (!image) return undefined;

			const aspect = image.naturalHeight / image.naturalWidth;
			const sprite = this.atlas.allocate(
				Math.ceil(pin.width * ratio),
				Math.ceil(pin.width * aspect * ratio),
				aspect,
			);
			const context = sprite.source.getContext("2d")!;
			drawTinted(context, image, pin, sprite.x, sprite.y, sprite.width, sprite.height);
			this.sprites.set(pin.spriteKey, sprite);
			return sprite;
		}

		private image(src: string) {
			let image = this.images.get(src);
			if (!image) {
				image = new Image();
				image.src = src;
				this.images.set(src, image);
				image
					.decode()
					.then(() => this.queueRedraw())
					.catch(() => undefined);
			}
			return image.complete && image.naturalWidth > 0 ? image : undefined;
		}

		private queueRedraw() {
			if (this.redrawQueued) return;
			this.redrawQueued = true;
			requestAnimationFrame(() => {
				this.redrawQueued = false;
				this.draw();
			});
		}

		private pinAt(latLng: google.maps.LatLng | null) {
			const point = latLng && this.getProjection()?.fromLatLngToContainerPixel(latLng);
			return point ? findPinAt(this.drawn, point.x, point.y, this.touchSlop) : undefined;
		}

		private hover(marker: Marker | undefined) {
			if (marker === this.hovered) return;
			this.hovered = marker;
			map.setOptions({ draggableCursor: marker ? "pointer" : null });
			if (marker?.tooltip) map.getDiv().title = marker.tooltip;
			else map.getDiv().removeAttribute("title");
		}
	}

	const layer = new CanvasPinLayer();
	layer.setMap(map);
	return {
		setPins: (markers) => layer.setPins(markers),
		remove: () => layer.setMap(null),
	};
}
